import { useState } from 'react';
import { Button } from '@/components/ui';
import type { ProjectView, ProposalView } from '@/types/current-project';
import type { useProjectCommand } from '@/hooks/useProjectCommand';
import { milestoneBudget } from '@/lib/project-presentation';
import { useDaoConfig } from '@/hooks/useDaoConfig';

export function ProposalReviewActions({
  project,
  proposal,
  command,
}: {
  project: ProjectView;
  proposal: ProposalView;
  command: ReturnType<typeof useProjectCommand>;
}) {
  const { currency } = useDaoConfig();
  const [reviewed, setReviewed] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [changesReviewed, setChangesReviewed] = useState<string | null>(null);
  const total = proposal.milestones
    .reduce((sum, m) => sum + BigInt(milestoneBudget(m)), 0n)
    .toString();
  const reviewKey = JSON.stringify(proposal);
  const locked = command.busy || command.pending || command.blocked;
  if (
    proposal.status !== 'PendingApproval' ||
    project.cancelled ||
    project.completed ||
    project.planning.frozen ||
    project.proposals.some((p) => p.status === 'Approved')
  )
    return null;
  return (
    <section
      className="backendForm space-y-4 border-t border-input pt-5"
      aria-label={'Review ' + proposal.title}
    >
      <h4 className="font-semibold">Execution review · {total} {currency.symbol}</h4>
      <a
        className="text-primary underline"
        href={'/payments/' + project.projectId + '/fund'}
      >
        View balance and request funding
      </a>
      <p className="text-sm text-muted-foreground">
        Execution approval locks this total in escrow. The server selects
        workers using skills, capacity and scores; each staffing slot needs a
        distinct worker within its milestone. Tasks track work separately.
      </p>
      {project.planning.status !== 'Completed' && (
        <p>
          First accept the delivered plan below to settle its planning payment.
        </p>
      )}
      <label className="backendCheck">
        <input
          type="checkbox"
          disabled={locked}
          checked={reviewed === reviewKey}
          onChange={(event) =>
            setReviewed(event.target.checked ? reviewKey : null)
          }
        />
        I reviewed proposal revision {proposal.revision}, its requirements and its{' '}
        {total} {currency.symbol} execution cost.
      </label>
      <Button
        disabled={
          locked ||
          reviewed !== reviewKey ||
          project.planning.status !== 'Completed'
        }
        onClick={() => {
          if (reviewed !== reviewKey) return;
          void command.run({
            kind: 'proposal-approve',
            projectId: project.projectId,
            proposalId: proposal.proposalId,
            payload: { expectedRevision: proposal.revision },
          });
          setReviewed(null);
        }}
      >
        Approve and fund execution
      </Button>
      <details>
        <summary className="cursor-pointer font-semibold">
          Request proposal changes
        </summary>
        <form
          className="mt-4 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void command.run({
              kind: 'proposal-changes',
              projectId: project.projectId,
              proposalId: proposal.proposalId,
              payload: { reference },
            });
            setChangesReviewed(null);
          }}
        >
          <fieldset disabled={locked} className="space-y-4">
            <label>
              Change request
              <textarea
                aria-label="Change request"
                required
                value={reference}
                onChange={(event) => {
                  setReference(event.target.value);
                  setChangesReviewed(null);
                }}
              />
            </label>
            <p className="text-sm text-muted-foreground">
              This returns the proposal to Draft for the coordinator. The
              backend stores your request but does not accept a revision
              parameter for this action.
            </p>
            <label className="backendCheck">
              <input
                type="checkbox"
                checked={changesReviewed === reviewKey}
                onChange={(event) =>
                  setChangesReviewed(event.target.checked ? reviewKey : null)
                }
              />
              I reviewed the changes requested for revision {proposal.revision}.
            </label>
            <Button
              type="submit"
              disabled={!reference.trim() || changesReviewed !== reviewKey}
            >
              Send change request
            </Button>
          </fieldset>
        </form>
      </details>
    </section>
  );
}
